// LIB
import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';

// STYLES
import {
  colors,
  fontFamily,
  fontSize,
} from '../../../Design_System/DesignSystem';

// COMPONENT
import { Text } from '../../atoms/Text';
import { Icon } from '../../atoms/Icon';

import { itemsPensee } from './ItemPensee';
import { itemsLogs } from './itemsLogs';
import { getPersonnes } from '../../../firebaseServices/firebaseServices';

// Type pour les objets de itemsPensee
interface Pensee {
  name: string;
  date: string; // Vous pouvez également utiliser Date si vous manipulez les dates
}

// Type pour les objets de itemsLogs
interface Log {
  date: string; // Même remarque que ci-dessus
}

// Type pour les objets de itemsGens
interface Personne {
  name: string;
  onContact: boolean;
}
// Définition de l'interface pour les éléments
type Item =
  | Pensee
  | Log // Pour itemsLogs
  | Personne;

// Type guard pour vérifier si un Item est une Personne
const isPersonne = (item: Item): item is Personne => {
  return (item as Personne).name !== undefined;
};

// Fonction pour trier les éléments par ordre alphabétique
const sortItemsAlphabetically = (items: Item[]): Item[] => {
  return items.sort((a, b) => {
    if (isPersonne(a) && isPersonne(b)) {
      return a.name.localeCompare(b.name);
    }
    // Autres logiques de tri si nécessaire, sinon retourner 0 pour les autres types
    return 0; // Si ce n'est ni Personne ni Pensee, on ne les compare pas.
  });
};

interface PrivateBlogInterface {
  setSelectedItem: React.Dispatch<React.SetStateAction<number>>;
  setBlog: (item: Pensee | Personne | Log) => void; // Accepte seulement Pensee ou Personne
}
const PrivateBlog = ({ setSelectedItem, setBlog }: PrivateBlogInterface) => {
  const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth < 843);
  const [selectedTheme, setSelectedTheme] = useState(0);
  const [oneHover, setOneHover] = useState(false);
  const [visibleSection, setVisibleSection] = useState<number | null>(1);
  const [isSectionVisible, setIsSectionVisible] = useState(true);
  const userEmail = localStorage.getItem(
    'a2b9fd36-29a5-41c9-8de9-49ce7e7c9c3c'
  ) as string;
  const [personnes, setPersonnes] = useState<Personne[]>([]);

  useEffect(() => {
    const fetchPersonnes = async () => {
      const fetchedPersonnes = await getPersonnes();
      setPersonnes(fetchedPersonnes);
    };

    fetchPersonnes();
  }, []);

  useEffect(() => {
    const handleResize = () => setIsSmallScreen(window.innerWidth < 843);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const toggleSectionVisibility = (themeId: number) => {
    if (visibleSection === themeId) {
      // Animation de fermeture
      setIsSectionVisible(false);
      setTimeout(() => {
        setVisibleSection(null);
      }, 500); // Assurez-vous que ce délai correspond à la durée de votre animation
    } else {
      setIsSectionVisible(false); // Appliquez la fermeture à la section précédente
      setTimeout(() => {
        setVisibleSection(themeId);
        setIsSectionVisible(true); // Montrez la nouvelle section après un court délai
      }, 500); // Même délai que l'animation
    }
  };

  return (
    <Wrapper>
      <Container>
        <Section>
          <Title onClick={() => toggleSectionVisibility(1)}>
            <CustomIcon
              size={fontSize.mini}
              icon="emptyDot"
              color={colors.tan}
            />
            <UnderlineText
              color={colors.tan}
              fontSize={fontSize.small}
              fontFamily={fontFamily.tinos}
              bold={true}
              justify={false}
            >
              Mes pensées
            </UnderlineText>
          </Title>
          {visibleSection === 1 && (
            <FadeInContainer isVisible={isSectionVisible}>
              {itemsPensee
                .filter((personne) => personne.consultingBy.includes(userEmail))
                .map((personne, index) => (
                  <ProjectContainer
                    key={index}
                    index={index}
                    onMouseEnter={() => {
                      setOneHover(true);
                      setSelectedTheme(21 + index);
                    }}
                    onMouseLeave={() => {
                      setOneHover(false);
                      setSelectedTheme(0);
                    }}
                    oneHover={oneHover}
                    selectedItem={selectedTheme === 21 + index}
                    onClick={() => {
                      setSelectedItem(8);
                      setBlog(personne as Pensee); // S'assurer que personne est de type Pensee
                    }}
                  >
                    <Date
                      color={colors.tan}
                      fontSize={fontSize.small}
                      fontFamily={fontFamily.tinos}
                      bold={true}
                      justify={false}
                    >
                      {personne.date}
                    </Date>
                    <Text
                      color={colors.tan}
                      fontSize={isSmallScreen ? fontSize.small : fontSize.title}
                      fontFamily={fontFamily.tinos}
                      bold={false}
                      justify={false}
                    >
                      / {personne.name}
                    </Text>
                  </ProjectContainer>
                ))}
            </FadeInContainer>
          )}
        </Section>
        <Section>
          <Title onClick={() => toggleSectionVisibility(2)}>
            <CustomIcon
              size={fontSize.mini}
              icon="emptyDot"
              color={colors.tan}
            />
            <UnderlineText
              color={colors.tan}
              fontSize={fontSize.small}
              fontFamily={fontFamily.tinos}
              bold={true}
              justify={false}
            >
              Logs
            </UnderlineText>
          </Title>
          {visibleSection === 2 && (
            <FadeInContainer isVisible={isSectionVisible}>
              {itemsLogs.map((log, index) => (
                <ProjectContainer
                  key={index}
                  index={index}
                  onMouseEnter={() => {
                    setOneHover(true);
                    setSelectedTheme(21 + index);
                  }}
                  onMouseLeave={() => {
                    setOneHover(false);
                    setSelectedTheme(0);
                  }}
                  oneHover={oneHover}
                  selectedItem={selectedTheme === 21 + index}
                  onClick={() => {
                    setSelectedItem(8);
                    setBlog(log as Log); // S'assurer que personne est de type Pensee
                  }}
                >
                  <Date
                    color={colors.tan}
                    fontSize={isSmallScreen ? fontSize.small : fontSize.title}
                    fontFamily={fontFamily.tinos}
                    bold={true}
                    justify={false}
                  >
                    {log.date}
                  </Date>
                </ProjectContainer>
              ))}
            </FadeInContainer>
          )}
        </Section>
        <Section>
          <Title onClick={() => toggleSectionVisibility(21)}>
            <CustomIcon
              size={fontSize.mini}
              icon="emptyDot"
              color={colors.tan}
            />
            <UnderlineText
              color={colors.tan}
              fontSize={fontSize.small}
              fontFamily={fontFamily.tinos}
              bold={true}
              justify={false}
            >
              Les Personnes
            </UnderlineText>
            {userEmail === 'tristan.lemire.sio@gmail.com' && (
              <AddGens onClick={() => setSelectedItem(9)}>
                <UnderlineText
                  color={colors.tan}
                  fontSize={fontSize.small}
                  fontFamily={fontFamily.tinos}
                  bold={true}
                  justify={false}
                >
                  Ajouter une personne
                </UnderlineText>
              </AddGens>
            )}
          </Title>
          {visibleSection === 21 && (
            <FadeInContainer isVisible={isSectionVisible}>
              {sortItemsAlphabetically(personnes).map((personne, index) => (
                <ProjectContainer
                  key={index}
                  index={index}
                  onMouseEnter={() => {
                    setOneHover(true);
                    setSelectedTheme(21 + index);
                  }}
                  onMouseLeave={() => {
                    setOneHover(false);
                    setSelectedTheme(0);
                  }}
                  oneHover={oneHover}
                  selectedItem={selectedTheme === 21 + index}
                  onClick={() => {
                    setSelectedItem(8);
                    setBlog(personne as Personne); // S'assurer que personne est de type Pensee
                  }}
                >
                  {isPersonne(personne) ? ( // Utilisez le type guard ici
                    <Text
                      color={colors.tan}
                      fontSize={isSmallScreen ? fontSize.small : fontSize.title}
                      fontFamily={fontFamily.tinos}
                      bold={false}
                      justify={false}
                    >
                      {personne.name}
                    </Text>
                  ) : null}
                </ProjectContainer>
              ))}
            </FadeInContainer>
          )}
        </Section>
      </Container>
    </Wrapper>
  );
};

// Animation keyframes pour l'ouverture
const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

// Animation keyframes pour la fermeture
const fadeOut = keyframes`
  from {
    opacity: 1;
    transform: translateY(0);
  }
  to {
    opacity: 0;
    transform: translateY(20px);
  }
`;

const FadeInContainer = styled.div<{ isVisible: boolean }>`
  display: flex;
  flex-direction: column;
  animation: ${(props) => (props.isVisible ? fadeIn : fadeOut)} 0.5s ease
    forwards;
`;

const AddGens = styled.div`
  margin-left: 10px;
`;

const ProjectContainer = styled.div<{
  selectedItem: boolean;
  oneHover: boolean;
  index: number;
}>`
  display: flex;
  justify-content: end;
  align-items: center;
  opacity: 0;
  animation: ${fadeIn} 0.5s ease forwards;
  animation-delay: ${(props) => props.index * 0.2}s;

  ${({ selectedItem }) =>
    selectedItem &&
    `
    opacity: 1;
  `}

  @media (min-width: 776px) {
    cursor: pointer;
    filter: ${(props) =>
      !props.selectedItem && props.oneHover ? 'blur(5px)' : 'none'};
    transition: filter 0.3s ease;
  }

  @media (max-width: 1265px) {
    justify-content: start;
    padding-top: 6px;
  }
`;

const Wrapper = styled.div`
  height: 75vh;
  overflow-y: scroll;
  scrollbar-width: thin;
  scrollbar-color: ${colors.tan} transparent;
  margin-right: 10px;
  transform: perspective(1000px) rotateY(-30deg);

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-thumb {
    background-color: ${colors.tan};
    border-radius: 10px;
  }

  &::-webkit-scrollbar-track {
    background-color: transparent;
  }

  @media (min-height: 1000px) {
    height: 67.9vh;
  }

  @media (max-width: 1265px) {
    transform: perspective(0) rotateY(0);
  }
`;

const Section = styled.div`
  margin: 20px;
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;

  @media (max-width: 1265px) {
    margin: 0;
  }
`;

const Date = styled(Text)`
  margin-right: 20px;
  font-weight: bold;

  @media (max-width: 1265px) {
    margin-right: 4px;
    font-weight: normal;
  }
`;

const Title = styled.div`
  display: flex;
  justify-content: end;
  cursor: pointer;
  position: relative;

  @media (max-width: 1091px) {
    justify-content: start;
  }
`;

const UnderlineText = styled(Text)`
  position: relative;
  &::after {
    content: '';
    position: absolute;
    left: 0;
    bottom: -2px;
    width: 0%;
    height: 2px;
    background-color: ${colors.tan};
    transition: width 0.4s ease-in-out;
  }

  &:hover::after {
    width: 100%;
  }

  @media (max-width: 600px) {
    font-size: ${fontSize.small};
  }
`;

const CustomIcon = styled(Icon)`
  margin-right: 5px;
  @media (max-width: 1265px) {
    display: none;
  }
`;

export default PrivateBlog;
